import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import axios from "axios";
import "./QuestionBankLogin.css";

const baseAPIUrl = process.env.REACT_APP_API_BASE_URL;

const slidesData = [
  "/pictures/qb-login-1.jpg",
  "/pictures/qb-login-2.jpg",
];

const QuestionBankLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedSchool, setSelectedSchool] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [search, setSearch] = useState("");

  const [activeTab, setActiveTab] = useState("qb-school");
  const [currentIndex, setCurrentIndex] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const studentsPerPage = 10;

  // Auto-slider effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % slidesData.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Scroll to specific section if state is set
  useEffect(() => {
    const sectionId = location.state?.scrollTo;
    if (!sectionId) return;

    const timer = setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 150);

    return () => clearTimeout(timer);
  }, [location.key]);


  // Load schools on modal open (or component mount)
  useEffect(() => {
    const loadSchools = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${baseAPIUrl}admin/general_settings/get_schools_from_techno_ace/`,
          { withCredentials: true }
        );

        if (res.data.status === 1 && res.data.data) {
          const schoolList = res.data.data;
          setSchools(schoolList);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadSchools();
  }, []);

  // When school changes → load classes
  const handleSchoolChange = async (e) => {
    const schoolName = e.target.value;

    setSelectedSchool(schoolName);
    setSelectedClass("");
    setStudents([]);
    setClasses([]);

    if (!schoolName) return;

    try {
      const res = await axios.post(
        `${baseAPIUrl}admin/general_settings/get_classes_from_techno_ace`,
        { school_name: schoolName },
        { withCredentials: true }
      );

      if (res.data.status === 1) {
        setClasses(res.data.data || []);
      } else {
        setClasses([]);
      }

    } catch (error) {
      console.error(error);
      setClasses([]);
    }
  };


  // When class changes → load students
  const handleClassChange = async (e) => {
    const className = e.target.value;
    setSelectedClass(className);

    if (!selectedSchool || !className) return;

    try {
      const res = await axios.post(
        `${baseAPIUrl}admin/general_settings/get_login_ids`,
        {
          school_name: selectedSchool,
          class_name: className
        },
        { withCredentials: true }
      );

      if (res.data.status === 1) {
        setStudents(res.data.data || []);
        setCurrentPage(1);
      } else {
        setStudents([]);
      }

    } catch (error) {
      console.error(error);
      setStudents([]);
    }
  };


  // Filter students
  const filteredStudents = students.filter(student =>
    (student.student_name?.toLowerCase() || "").includes(search.toLowerCase()) ||
    (student.login_id?.toLowerCase() || "").includes(search.toLowerCase())
  );

  const indexOfLast = currentPage * studentsPerPage;
  const indexOfFirst = indexOfLast - studentsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);


  // Copy login id
  const copyLoginId = (loginId) => {
    navigator.clipboard.writeText(loginId);
  };

  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {

    const checkSession = async () => {

      const res = await axios.get(
        `${baseAPIUrl}admin/general_settings/check_study_mat_session`,
        { withCredentials: true }
      );

      if (res.data.status === "logged_in") {
        navigate("/question-bank");
      }

      if (res.data.status === "reset_required") {
        navigate("/set-tigps-question-bank-password");
      }
    };

    checkSession();

  }, []);

  const handleLogin = async () => {

    if (!loginId.trim()) {
      setErrorMsg("Please enter correct Login id.");
      return;
    }

    if (!password.trim()) {
      setErrorMsg("Please enter password.");
      return;
    }

    setErrorMsg("");
    setLoginLoading(true);

    try {
      const res = await axios.post(
        `${baseAPIUrl}admin/general_settings/tigps_question_bank_login_submit`,
        { login_id: loginId, password },
        { withCredentials: true }
      );

      if (res.data.result_fetch === 1) {
        navigate("/set-tigps-question-bank-password");
      }
      else if (res.data.result_fetch === 2) {
        navigate("/question-bank");
      }
      else {
        setErrorMsg(res.data.msg);
      }

    } catch (error) {
      setErrorMsg("Server error. Please try again.");
    } finally {
      setLoginLoading(false);
    }
  };


  return (
    <>
      <div className="qblogin-page">
        {/* <Hero image="/pictures/image.jpg" alt="TIGPS Academics" /> */}

        {/* ===== QB Login Section ===== */}
        <div className="qblogin-container ">

          {/* ===== Image Slider Section ===== */}
          <div className="qblogin-image-section">
            <div className="qblogin-slider">
              {slidesData.map((img, index) => (
                <div
                  key={index}
                  className={`qblogin-slide ${index === currentIndex ? "active" : ""}`}
                  style={{ backgroundImage: `url(${img})` }}
                ></div>
              ))}
            </div>

            <div className="qblogin-image-overlay">

              <div className="qblogin-image-text">
                <h3>One login. All your school essentials.</h3>
                <div className="qblogin-dots">
                  {slidesData.map((_, index) => (
                    <span
                      key={index}
                      className={`qblogin-dot ${index === currentIndex ? "active" : ""}`}
                      onClick={() => setCurrentIndex(index)}
                    ></span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ===== Login Form Section ===== */}
          <div className="qblogin-login-section">
            <h1 className="qblogin-title text-center" style={{ fontFamily: 'Lora', fontSize: '1.8rem' }}>Sign In</h1>

            <p>Username</p>
            <input
              className="qblogin-input"
              type="text"
              placeholder="Username"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
            />

            <p>Password</p>
            <input
              className="qblogin-input"
              type="password"
              placeholder="********"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            {errorMsg && (
              <p style={{ color: "red" }}>
                {errorMsg}
              </p>
            )}

            <button
              className="qblogin-login-btn"
              onClick={handleLogin}
              disabled={loginLoading}
            >
              {loginLoading ? "Logging in..." : "Login"}
            </button>


            <div>
              <p
                className="qblogin-forgot text-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="modal"
                data-bs-target="#forgetLoginIdModal"
              >
                Don't know your <span>login id</span> or <span>password?</span>
              </p>
            </div>
          </div>
        </div>
      </div>




      <div
        className="modal fade"
        id="forgetLoginIdModal"
        tabIndex="-1"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable custom-modal-width">
          <div className="modal-content">

            <div className="modal-header">
              <h5 className="modal-title">
                Retrieve Login ID & Password
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>

            <div className="modal-body">

              <div className="row g-3">
                {/* School Dropdown */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">School Name</label>
                  <select
                    className="form-select"
                    value={selectedSchool}
                    onChange={handleSchoolChange}
                  >
                    <option value="">Select School</option>
                    {schools.map((s, index) => (
                      <option key={index} value={s.school_name}>
                        {s.school_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Class Dropdown */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Class Name</label>
                  <select
                    className="form-select"
                    value={selectedClass}
                    onChange={handleClassChange}
                  >
                    <option value="">Select Class</option>
                    {classes.map((c, index) => (
                      <option key={index} value={c.class_name}>
                        {c.class_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <p style={{ fontSize: "18px" }}>
                  <span style={{ color: "red" }}>*</span> Your default password{" "}
                  <span style={{ fontWeight: "bold", color: "#85191a" }}>
                    ABCD@1234
                  </span>
                </p>
              </div>

              {/* Search */}
              {students.length > 0 && (
                <input
                  type="text"
                  placeholder="Search student..."
                  className="form-control mb-3"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              )}

              {/* Table */}
              {currentStudents.length > 0 ? (
                <div className="table-responsive" style={{ maxHeight: "350px" }}>
                  <table className="table table-bordered table-sm">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Login ID</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentStudents.map((student, index) => (
                        <tr key={index}>
                          <td>{student.student_name}</td>
                          <td>{student.login_id}</td>
                          <td>
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => copyLoginId(student.login_id)}
                            >
                              Copy
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <button
                        className="btn btn-sm btn-secondary"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => prev - 1)}
                      >
                        Previous
                      </button>

                      <span>
                        Page {currentPage} of {totalPages}
                      </span>

                      <button
                        className="btn btn-sm btn-secondary"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              ) : students.length > 0 ? (
                <p className="text-center">No data found.</p>
              ) : null}

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuestionBankLogin;
